"use client";

import React, { useState, useEffect } from "react";
import {
  ShieldCheck,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Download,
  Plus,
  RefreshCw,
  Building2,
  Globe,
  DollarSign,
  Receipt,
  Eye,
  AlertCircle,
  FileCheck,
  Percent,
  Landmark,
  FileSignature,
  Sliders,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/Button";
import { SellerTaxProfile } from "@/lib/complianceConfig";
import { InvoiceRecord } from "@/lib/db";
import { LEGAL_DOCUMENTS } from "@/lib/legalDocuments";

const BLANK_PROFILE: SellerTaxProfile = {
  legalBusinessName: "",
  tradeName: "",
  pan: "",
  udyamRegistrationNumber: "",
  addressLine1: "",
  city: "",
  state: "",
  stateCode: "",
  country: "IN",
  postalCode: "",
  gstStatus: "unregistered",
  gstin: "",
  registeredUnderLut: false,
  lutNumber: "",
  lutValidFrom: "",
  lutValidTo: "",
  sacCode: "",
  defaultGstRate: 18.0,
  cgstRate: 9.0,
  sgstRate: 9.0,
  igstRate: 18.0,
  gstThreshold: 2000000,
  gstThresholdSpecial: 1000000,
  baseSettlementCurrency: "INR",
  supportedCurrencies: ["INR", "USD", "EUR", "GBP", "AED"],
  bankName: "",
  bankAccountNumber: "",
  bankIfscCode: "",
  bankSwiftBic: "",
  bankBranch: "",
  rbiPurposeCode: "",
  trcDocumentUrl: null,
  form10fUrl: null,
  updatedAt: new Date().toISOString(),
};

export default function ComplianceTab() {
  const [loading, setLoading] = useState(true);
  const [sellerProfile, setSellerProfile] = useState<SellerTaxProfile>(BLANK_PROFILE);
  const [turnoverData, setTurnoverData] = useState<any>(null);
  const [invoices, setInvoices] = useState<InvoiceRecord[]>([]);
  const [remittances, setRemittances] = useState<any[]>([]);
  const [consents, setConsents] = useState<any[]>([]);
  const [activeSubTab, setActiveSubTab] = useState<"turnover" | "invoices" | "profile" | "remittances" | "consents">("turnover");

  // Invoice creation modal
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [customerName, setCustomerName] = useState("");
  const [customerCompany, setCustomerCompany] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [customerCountry, setCustomerCountry] = useState("IN");
  const [customerState, setCustomerState] = useState("");
  const [customerStateCode, setCustomerStateCode] = useState("");
  const [customerTaxId, setCustomerTaxId] = useState("");
  const [currency, setCurrency] = useState("INR");
  const [fxRate, setFxRate] = useState(1.0);
  const [serviceDescription, setServiceDescription] = useState("");
  const [serviceRate, setServiceRate] = useState(0);
  const [whtPercent, setWhtPercent] = useState(0);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [createError, setCreateError] = useState("");

  // Saving profile state
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileSuccess, setProfileSuccess] = useState("");

  const fetchData = async () => {
    setLoading(true);
    try {
      // 1. Fetch Profile
      const profRes = await fetch("/api/crm/compliance/profile");
      if (profRes.ok) {
        const pData = await profRes.json();
        if (pData.sellerProfile) setSellerProfile(pData.sellerProfile);
      }

      // 2. Fetch Turnover
      const turnRes = await fetch("/api/crm/compliance/turnover");
      if (turnRes.ok) {
        const tData = await turnRes.json();
        setTurnoverData(tData);
      }

      // 3. Fetch Invoices
      const invRes = await fetch("/api/crm/invoices?limit=50");
      if (invRes.ok) {
        const iData = await invRes.json();
        setInvoices(iData.invoices || []);
      }

      // 4. Fetch Remittances
      const remRes = await fetch("/api/crm/compliance/remittances");
      if (remRes.ok) {
        const rData = await remRes.json();
        setRemittances(rData.remittances || []);
      }

      // 5. Fetch Consents
      const conRes = await fetch("/api/crm/consent");
      if (conRes.ok) {
        const cData = await conRes.json();
        setConsents(cData.consents || []);
      }
    } catch (e) {
      console.error("[Compliance Tab Fetch Error]:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileSuccess("");
    try {
      const res = await fetch("/api/crm/compliance/profile", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sellerProfile }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.sellerProfile) setSellerProfile(data.sellerProfile);
        setProfileSuccess("Compliance tax profile saved successfully!");
        fetchData(); // Refresh turnover and calculations
      }
    } catch {
      alert("Failed to update tax profile");
    } finally {
      setSavingProfile(false);
    }
  };

  const handleCreateInvoice = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingInvoice(true);
    setCreateError("");

    try {
      const res = await fetch("/api/crm/invoices", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          customerName,
          customerCompany,
          customerEmail,
          customerCountry,
          customerState,
          customerStateCode,
          customerTaxId,
          currency,
          fxRate,
          customWhtPercent: whtPercent,
          lineItems: [
            {
              description: serviceDescription,
              sacCode: sellerProfile.sacCode,
              quantity: 1,
              unitPrice: Number(serviceRate),
              total: Number(serviceRate),
            },
          ],
        }),
      });

      const data = await res.json();
      if (res.ok && data.success) {
        setIsCreateModalOpen(false);
        fetchData();
        // Reset form
        setCustomerName("");
        setCustomerCompany("");
        setCustomerEmail("");
        setCustomerTaxId("");
      } else {
        setCreateError(data.error || "Failed to generate invoice");
      }
    } catch {
      setCreateError("Network error while creating invoice.");
    } finally {
      setCreatingInvoice(false);
    }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <div className="w-8 h-8 border-3 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-xs text-slate-500 font-medium">Loading compliance &amp; billing engine...</p>
      </div>
    );
  }

  const isGstRegistered = sellerProfile.gstStatus === "registered";
  const turnover = turnoverData?.turnover;
  const threshold = turnoverData?.threshold || 2000000;
  const percentTurnover = turnoverData?.percentOfThreshold || 0;
  const alertStatus = turnoverData?.alertStatus || "NORMAL";

  return (
    <div className="space-y-6">
      {/* ------------------------------------------------------------- */}
      {/* 1. TOP HERO: GST TURNOVER TRACKER WIDGET                      */}
      {/* ------------------------------------------------------------- */}
      <div className="p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm relative overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <span className="px-2.5 py-1 rounded-full text-[11px] font-bold bg-primary/10 text-primary border border-primary/20">
                FY {turnoverData?.financialYear || "2026-27"}
              </span>
              <span
                className={`px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                  !isGstRegistered
                    ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20"
                    : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                }`}
              >
                {!isGstRegistered ? "Udyam MSME (Unregistered under GST)" : `GST Registered (${sellerProfile.gstin})`}
              </span>
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mt-2">
              GST Turnover Tracker &amp; Threshold Monitor
            </h2>
            <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
              Aggregates all domestic and foreign export invoices per Indian Financial Year (Apr 1 – Mar 31).
            </p>
          </div>

          <div className="text-right">
            <div className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white font-mono">
              ₹{(turnover?.totalTurnoverINR || 0).toLocaleString("en-IN")}
            </div>
            <div className="text-xs font-semibold text-slate-400 mt-0.5">
              of ₹{threshold.toLocaleString("en-IN")} Statutory Threshold
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-5">
          <div className="flex items-center justify-between text-xs font-semibold mb-2">
            <span className="text-slate-500 dark:text-slate-400">
              Registration Trigger: {percentTurnover}% Utilized
            </span>
            <span
              className={
                alertStatus === "EXCEEDED_100_PERCENT"
                  ? "text-rose-500 font-bold"
                  : alertStatus === "WARNING_80_PERCENT"
                  ? "text-amber-500 font-bold"
                  : "text-emerald-500 font-bold"
              }
            >
              {alertStatus === "EXCEEDED_100_PERCENT"
                ? "Threshold Exceeded (Mandatory GST Action)"
                : alertStatus === "WARNING_80_PERCENT"
                ? "Approaching Threshold (80%+ Alert)"
                : `₹${(turnoverData?.remainingBeforeThreshold || 0).toLocaleString("en-IN")} remaining`}
            </span>
          </div>

          <div className="w-full h-3.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden p-0.5 border border-slate-200 dark:border-slate-700">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                alertStatus === "EXCEEDED_100_PERCENT"
                  ? "bg-rose-500"
                  : alertStatus === "WARNING_80_PERCENT"
                  ? "bg-amber-500"
                  : "bg-gradient-to-r from-cyan-500 to-emerald-500"
              }`}
              style={{ width: `${Math.min(100, percentTurnover)}%` }}
            />
          </div>
        </div>

        {/* Turnover Alerts */}
        {turnoverData?.alertMessage && (
          <div
            className={`mt-4 p-3.5 rounded-xl border text-xs flex items-start gap-2.5 ${
              alertStatus === "EXCEEDED_100_PERCENT"
                ? "bg-rose-500/10 border-rose-500/20 text-rose-600 dark:text-rose-400 font-semibold"
                : "bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400 font-semibold"
            }`}
          >
            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>{turnoverData.alertMessage}</div>
          </div>
        )}

        {/* Breakdown Metric Pills */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-5 pt-4 border-t border-slate-100 dark:border-slate-800 text-xs">
          <div>
            <div className="text-slate-400 text-[11px]">Domestic Invoices</div>
            <div className="font-bold text-slate-800 dark:text-slate-200 font-mono mt-0.5">
              ₹{(turnover?.domesticTurnoverINR || 0).toLocaleString("en-IN")}
            </div>
          </div>
          <div>
            <div className="text-slate-400 text-[11px]">Foreign Export Invoices</div>
            <div className="font-bold text-slate-800 dark:text-slate-200 font-mono mt-0.5">
              ₹{(turnover?.exportTurnoverINR || 0).toLocaleString("en-IN")}
            </div>
          </div>
          <div>
            <div className="text-slate-400 text-[11px]">Total Issued</div>
            <div className="font-bold text-slate-800 dark:text-slate-200 font-mono mt-0.5">
              {turnover?.totalInvoiceCount || 0} Invoices
            </div>
          </div>
          <div>
            <div className="text-slate-400 text-[11px]">Paid / Realized</div>
            <div className="font-bold text-emerald-600 dark:text-emerald-400 font-mono mt-0.5">
              {turnover?.paidInvoiceCount || 0} Invoices
            </div>
          </div>
        </div>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* 2. SUB-NAVIGATION TABS                                         */}
      {/* ------------------------------------------------------------- */}
      <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
        <div className="flex items-center gap-2 overflow-x-auto scrollbar-none">
          {[
            { id: "turnover", name: "Overview & Metrics", icon: Receipt },
            { id: "invoices", name: "Invoices & Billing", icon: FileText },
            { id: "profile", name: "Tax & Identity Profile", icon: Building2 },
            { id: "remittances", name: "FIRC / Foreign Inward", icon: Landmark },
            { id: "consents", name: "Legal Consents Audit", icon: FileSignature },
          ].map((tab) => {
            const Icon = tab.icon;
            const active = activeSubTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveSubTab(tab.id as any)}
                className={`px-3.5 py-1.5 rounded-xl text-xs font-bold flex items-center gap-2 transition-all cursor-pointer whitespace-nowrap ${
                  active
                    ? "bg-slate-900 text-white dark:bg-white dark:text-slate-900 shadow-sm"
                    : "text-slate-500 hover:text-slate-900 dark:hover:text-white"
                }`}
              >
                <Icon className="w-3.5 h-3.5" />
                {tab.name}
              </button>
            );
          })}
        </div>

        <Button
          size="sm"
          onClick={() => setIsCreateModalOpen(true)}
          className="text-xs flex items-center gap-1.5 h-8 bg-gradient-to-r from-primary to-blue-600 text-white font-bold cursor-pointer shrink-0"
        >
          <Plus className="w-3.5 h-3.5" />
          Issue Invoice
        </Button>
      </div>

      {/* ------------------------------------------------------------- */}
      {/* 3. TAB CONTENT: INVOICES MANAGER                              */}
      {/* ------------------------------------------------------------- */}
      {(activeSubTab === "turnover" || activeSubTab === "invoices") && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-sm text-slate-900 dark:text-white">
              Issued Invoices ({invoices.length})
            </h3>
            <span className="text-[11px] text-slate-400 font-mono">
              Format: {isGstRegistered ? "Tax Invoice (GSTIN/SAC)" : "Bill of Supply (Udyam MSME)"}
            </span>
          </div>

          <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="bg-slate-50 dark:bg-slate-950/60 border-b border-slate-200 dark:border-slate-800 text-slate-500 uppercase font-semibold">
                  <tr>
                    <th className="px-4 py-3">Invoice No</th>
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Customer &amp; Country</th>
                    <th className="px-4 py-3">Type</th>
                    <th className="px-4 py-3">Total Amount</th>
                    <th className="px-4 py-3">INR Equiv.</th>
                    <th className="px-4 py-3">Tax Scenario</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3 text-right">PDF</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800/80">
                  {invoices.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="px-6 py-12 text-center text-slate-400 italic">
                        No invoices generated yet. Click &quot;Issue Invoice&quot; to generate your first compliant Bill of Supply.
                      </td>
                    </tr>
                  ) : (
                    invoices.map((inv) => (
                      <tr key={inv.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/50 transition-colors">
                        <td className="px-4 py-3 font-mono font-bold text-primary">
                          {inv.invoice_number}
                        </td>
                        <td className="px-4 py-3 text-slate-500 font-mono text-[11px]">
                          {inv.issue_date}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-bold text-slate-900 dark:text-white truncate max-w-[180px]">
                            {inv.customer_name}
                          </div>
                          <div className="text-[11px] text-slate-400 truncate">
                            {inv.customer_company || inv.customer_email} ({inv.customer_country})
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                              inv.invoice_type === "bill_of_supply"
                                ? "bg-cyan-500/10 text-cyan-600 dark:text-cyan-400 border-cyan-500/20"
                                : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/20"
                            }`}
                          >
                            {inv.invoice_type === "bill_of_supply" ? "Bill of Supply" : "Tax Invoice"}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-bold font-mono">
                          {inv.currency} {inv.total_amount.toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300 font-mono font-semibold">
                          ₹{inv.inr_equivalent.toLocaleString("en-IN")}
                        </td>
                        <td className="px-4 py-3 text-[11px] text-slate-500">
                          <span className="truncate block max-w-[160px]" title={inv.tax_scenario}>
                            {inv.tax_scenario.replace(/_/g, " ")}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                            {inv.status.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <a
                            href={`/api/crm/invoices/${inv.id}/pdf`}
                            download
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-slate-100 dark:bg-slate-800 text-primary hover:bg-primary/10 transition-colors"
                          >
                            <Download className="w-3.5 h-3.5" />
                            PDF
                          </a>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* 4. TAB CONTENT: SELLER TAX & IDENTITY PROFILE                 */}
      {/* ------------------------------------------------------------- */}
      {activeSubTab === "profile" && (
        <form onSubmit={handleSaveProfile} className="p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100 dark:border-slate-800">
            <div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">
                Seller Tax &amp; Identity Configuration
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Configured with sensible defaults and <code>// VERIFY_WITH_CA</code> tags for legal audit.
              </p>
            </div>
            {profileSuccess && (
              <span className="text-xs text-emerald-600 font-bold flex items-center gap-1">
                <CheckCircle2 className="w-4 h-4" />
                {profileSuccess}
              </span>
            )}
          </div>

          {/* MASTER GST SWITCH */}
          <div className="p-5 rounded-2xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800">
            <div className="flex items-start justify-between gap-4">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-primary">
                  Master Registration Switch
                </span>
                <h4 className="text-sm font-bold text-slate-900 dark:text-white mt-1">
                  Current GST Registration Status
                </h4>
                <p className="text-xs text-slate-500 mt-1 max-w-xl">
                  {sellerProfile.gstStatus === "unregistered"
                    ? "UNREGISTERED (Current): Operating under MSME Udyam. Invoices render as plain 'Bill of Supply' with zero GST lines, zero GSTIN, and no tax breakdown. Old invoices remain completely immutable."
                    : "REGISTERED: Full statutory GST engine active. Invoices render with GSTIN, SAC Code, CGST/SGST vs IGST breakdown, and Letter of Undertaking (LUT) export declarations."}
                </p>
              </div>

              <select
                value={sellerProfile.gstStatus}
                onChange={(e) =>
                  setSellerProfile({ ...sellerProfile, gstStatus: e.target.value as any })
                }
                className={`px-4 py-2 rounded-xl text-xs font-bold border cursor-pointer ${
                  sellerProfile.gstStatus === "unregistered"
                    ? "bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/30"
                    : "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border-emerald-500/30"
                }`}
              >
                <option value="unregistered">Unregistered (Udyam MSME Only)</option>
                <option value="registered">Registered under GST</option>
              </select>
            </div>
          </div>

          {/* Identity Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-xs">
            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Legal Business Name
              </label>
              <input
                type="text"
                value={sellerProfile.legalBusinessName}
                onChange={(e) => setSellerProfile({ ...sellerProfile, legalBusinessName: e.target.value })}
                placeholder="e.g. John Traders & Distribution LLC"
                className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-medium"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Permanent Account Number (PAN)
              </label>
              <input
                type="text"
                value={sellerProfile.pan}
                onChange={(e) => setSellerProfile({ ...sellerProfile, pan: e.target.value.toUpperCase() })}
                placeholder="e.g. ABCDE1234F"
                className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-mono font-bold"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                MSME Udyam Registration No
              </label>
              <input
                type="text"
                value={sellerProfile.udyamRegistrationNumber}
                onChange={(e) => setSellerProfile({ ...sellerProfile, udyamRegistrationNumber: e.target.value })}
                placeholder="e.g. UDYAM-XX-00-0000000 (Optional)"
                className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-mono"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                State &amp; GST State Code
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={sellerProfile.state}
                  onChange={(e) => setSellerProfile({ ...sellerProfile, state: e.target.value })}
                  placeholder="e.g. Maharashtra or Dubai"
                  className="flex-1 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-medium"
                />
                <input
                  type="text"
                  value={sellerProfile.stateCode}
                  onChange={(e) => setSellerProfile({ ...sellerProfile, stateCode: e.target.value })}
                  placeholder="27"
                  className="w-16 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-mono text-center font-bold"
                />
              </div>
            </div>

            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                GSTIN (Active when Registered)
              </label>
              <input
                type="text"
                value={sellerProfile.gstin || ""}
                disabled={!isGstRegistered}
                onChange={(e) => setSellerProfile({ ...sellerProfile, gstin: e.target.value.toUpperCase() })}
                placeholder={isGstRegistered ? "27ABCDE1234F1Z5" : "Disabled while Unregistered"}
                className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-mono disabled:opacity-50"
              />
            </div>

            <div>
              <label className="block font-semibold text-slate-700 dark:text-slate-300 mb-1">
                SAC / HSN Code
              </label>
              <input
                type="text"
                value={sellerProfile.sacCode}
                onChange={(e) => setSellerProfile({ ...sellerProfile, sacCode: e.target.value })}
                placeholder="e.g. 7208 or 998314"
                className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-mono"
              />
            </div>
          </div>

          {/* Export of Service / LUT Settings */}
          {isGstRegistered && (
            <div className="p-4 rounded-2xl bg-blue-50/50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/60 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold text-blue-900 dark:text-blue-300 flex items-center gap-1.5">
                  <Globe className="w-4 h-4" />
                  Letter of Undertaking (LUT) Export Configuration
                </h4>
                <label className="flex items-center gap-2 text-xs font-semibold cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sellerProfile.registeredUnderLut}
                    onChange={(e) => setSellerProfile({ ...sellerProfile, registeredUnderLut: e.target.checked })}
                    className="rounded border-slate-300 text-primary focus:ring-primary"
                  />
                  <span>Registered under LUT (Zero-Rated Export)</span>
                </label>
              </div>

              {sellerProfile.registeredUnderLut && (
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  <div>
                    <label className="block font-medium mb-1">LUT Reference Number</label>
                    <input
                      type="text"
                      value={sellerProfile.lutNumber || ""}
                      onChange={(e) => setSellerProfile({ ...sellerProfile, lutNumber: e.target.value })}
                      placeholder="AD290324001234A"
                      className="w-full px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 font-mono"
                    />
                  </div>
                  <div>
                    <label className="block font-medium mb-1">Valid From</label>
                    <input
                      type="date"
                      value={sellerProfile.lutValidFrom || ""}
                      onChange={(e) => setSellerProfile({ ...sellerProfile, lutValidFrom: e.target.value })}
                      className="w-full px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800"
                    />
                  </div>
                  <div>
                    <label className="block font-medium mb-1">Valid To</label>
                    <input
                      type="date"
                      value={sellerProfile.lutValidTo || ""}
                      onChange={(e) => setSellerProfile({ ...sellerProfile, lutValidTo: e.target.value })}
                      className="w-full px-3 py-1.5 rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Bank & Remittance Settings */}
          <div className="pt-2">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-1.5">
              <Landmark className="w-3.5 h-3.5" />
              Banking &amp; Foreign Remittance Details (For FIRC / Wire)
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-xs">
              <div>
                <label className="block font-medium mb-1">Bank Name</label>
                <input
                  type="text"
                  value={sellerProfile.bankName}
                  onChange={(e) => setSellerProfile({ ...sellerProfile, bankName: e.target.value })}
                  placeholder="e.g. HDFC Bank, Emirates NBD"
                  className="w-full px-3 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800"
                />
              </div>
              <div>
                <label className="block font-medium mb-1">Account Number</label>
                <input
                  type="text"
                  value={sellerProfile.bankAccountNumber}
                  onChange={(e) => setSellerProfile({ ...sellerProfile, bankAccountNumber: e.target.value })}
                  placeholder="e.g. 50200012345678"
                  className="w-full px-3 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-mono"
                />
              </div>
              <div>
                <label className="block font-medium mb-1">IFSC Code</label>
                <input
                  type="text"
                  value={sellerProfile.bankIfscCode}
                  onChange={(e) => setSellerProfile({ ...sellerProfile, bankIfscCode: e.target.value.toUpperCase() })}
                  placeholder="e.g. HDFC0001234"
                  className="w-full px-3 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-mono font-bold"
                />
              </div>
              <div>
                <label className="block font-medium mb-1">SWIFT / BIC</label>
                <input
                  type="text"
                  value={sellerProfile.bankSwiftBic}
                  onChange={(e) => setSellerProfile({ ...sellerProfile, bankSwiftBic: e.target.value.toUpperCase() })}
                  placeholder="e.g. HDFCINBBXXX"
                  className="w-full px-3 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-mono"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end pt-4 border-t border-slate-100 dark:border-slate-800">
            <Button
              type="submit"
              disabled={savingProfile}
              className="px-6 py-2 rounded-xl text-xs font-bold cursor-pointer"
            >
              {savingProfile ? "Saving Profile..." : "Save Compliance Profile"}
            </Button>
          </div>
        </form>
      )}

      {/* ------------------------------------------------------------- */}
      {/* 5. TAB CONTENT: REMITTANCES (FIRC/FIRA)                        */}
      {/* ------------------------------------------------------------- */}
      {activeSubTab === "remittances" && (
        <div className="p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">
                Foreign Inward Remittance Certificates (FIRC / e-FIRA)
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Bank proofs of foreign realization required for zero-rated export audit under Section 2(6) of IGST Act.
              </p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-950/60 border-b border-slate-200 dark:border-slate-800 text-slate-500 font-semibold">
                <tr>
                  <th className="px-4 py-3">ID</th>
                  <th className="px-4 py-3">Invoice ID</th>
                  <th className="px-4 py-3">Bank Ref</th>
                  <th className="px-4 py-3">Foreign Amount</th>
                  <th className="px-4 py-3">INR Realized</th>
                  <th className="px-4 py-3">FIRC Number</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {remittances.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-slate-400 italic">
                      No foreign remittances logged yet. When foreign export invoices are paid via SWIFT / international wire, attach FIRC bank certificates here.
                    </td>
                  </tr>
                ) : (
                  remittances.map((rem) => (
                    <tr key={rem.id}>
                      <td className="px-4 py-3 font-mono font-bold text-slate-400">#{rem.id}</td>
                      <td className="px-4 py-3 font-mono font-bold text-primary">Inv #{rem.invoice_id}</td>
                      <td className="px-4 py-3 text-slate-600 dark:text-slate-300">{rem.bank_reference || "—"}</td>
                      <td className="px-4 py-3 font-mono">{rem.currency} {rem.remitted_amount?.toLocaleString() || "0"}</td>
                      <td className="px-4 py-3 font-mono font-bold text-emerald-600">₹{rem.inr_realized?.toLocaleString("en-IN") || "0"}</td>
                      <td className="px-4 py-3 font-mono">{rem.firc_number || "Pending Bank Issue"}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">
                          {rem.status.toUpperCase()}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* 6. TAB CONTENT: LEGAL CONSENT AUDIT LOGS                       */}
      {/* ------------------------------------------------------------- */}
      {activeSubTab === "consents" && (
        <div className="p-6 rounded-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 shadow-sm space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-slate-900 dark:text-white">
                Immutable Click-Wrap Legal Consent Records
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                Cryptographic evidence logged during trial registration and paid checkout for compliance with India DPDP Act 2023 &amp; GDPR.
              </p>
            </div>
            <span className="text-xs font-mono font-bold text-slate-400">
              {consents.length} Consents Logged
            </span>
          </div>

          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-950/60 border-b border-slate-200 dark:border-slate-800 text-slate-500 font-semibold">
                <tr>
                  <th className="px-4 py-3">User ID</th>
                  <th className="px-4 py-3">Document</th>
                  <th className="px-4 py-3">Version</th>
                  <th className="px-4 py-3">IP Address</th>
                  <th className="px-4 py-3">Consent Type</th>
                  <th className="px-4 py-3">Cryptographic Hash (SHA-256)</th>
                  <th className="px-4 py-3">Timestamp</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-mono text-[11px]">
                {consents.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-slate-400 italic font-sans">
                      No user consent records logged yet. New user signups will generate verifiable consent tokens here.
                    </td>
                  </tr>
                ) : (
                  consents.map((c) => (
                    <tr key={c.id}>
                      <td className="px-4 py-3 font-bold text-slate-700 dark:text-slate-300">User #{c.user_id}</td>
                      <td className="px-4 py-3 font-sans font-bold text-primary">{c.document_slug.toUpperCase()}</td>
                      <td className="px-4 py-3 text-slate-500">v{c.document_version}</td>
                      <td className="px-4 py-3 text-slate-500">{c.ip_address}</td>
                      <td className="px-4 py-3 font-sans">
                        <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 dark:bg-slate-800">
                          {c.consent_type}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-400 truncate max-w-[140px]" title={c.consent_hash}>
                        {c.consent_hash.slice(0, 16)}...
                      </td>
                      <td className="px-4 py-3 text-slate-500">{c.created_at}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------- */}
      {/* 7. MODAL: ISSUE NEW INVOICE                                   */}
      {/* ------------------------------------------------------------- */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="w-full max-w-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 shadow-2xl space-y-5 my-8">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
              <div>
                <span className="text-[11px] font-bold uppercase tracking-wider text-primary">
                  Compliant Invoicing Engine
                </span>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white mt-0.5">
                  Issue Statutory Invoice
                </h3>
              </div>
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="w-8 h-8 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 hover:text-slate-900 dark:hover:text-white flex items-center justify-center cursor-pointer"
              >
                ✕
              </button>
            </div>

            {createError && (
              <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-600 text-xs">
                {createError}
              </div>
            )}

            <form onSubmit={handleCreateInvoice} className="space-y-4 text-xs">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold mb-1">Customer / Contact Name *</label>
                  <input
                    type="text"
                    required
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    placeholder="Vikram Malhotra"
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800"
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-1">Company / Entity Name</label>
                  <input
                    type="text"
                    value={customerCompany}
                    onChange={(e) => setCustomerCompany(e.target.value)}
                    placeholder="Horizon Real Estate LLP"
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold mb-1">Customer Email *</label>
                  <input
                    type="email"
                    required
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="vikram@horizonrealty.in"
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800"
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-1">Customer Country *</label>
                  <select
                    value={customerCountry}
                    onChange={(e) => {
                      const c = e.target.value;
                      setCustomerCountry(c);
                      if (c === "IN") {
                        setCurrency("INR");
                        setFxRate(1.0);
                      } else if (c === "AE") {
                        setCurrency("AED");
                        setFxRate(22.8);
                      } else {
                        setCurrency("USD");
                        setFxRate(87.5);
                      }
                    }}
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 cursor-pointer"
                  >
                    <option value="IN">India (Domestic)</option>
                    <option value="US">United States (Export)</option>
                    <option value="DE">Germany / EU (Export)</option>
                    <option value="GB">United Kingdom (Export)</option>
                    <option value="AE">United Arab Emirates (Export)</option>
                    <option value="SG">Singapore (Export)</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block font-semibold mb-1">
                    Customer Tax ID / GSTIN / VAT Number
                  </label>
                  <input
                    type="text"
                    value={customerTaxId}
                    onChange={(e) => setCustomerTaxId(e.target.value)}
                    placeholder={customerCountry === "IN" ? "29ABCDE1234F1Z5" : "EU VAT or EIN"}
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-mono"
                  />
                </div>
                <div>
                  <label className="block font-semibold mb-1">State / Jurisdiction</label>
                  <input
                    type="text"
                    value={customerState}
                    onChange={(e) => setCustomerState(e.target.value)}
                    placeholder="Karnataka / California"
                    className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800"
                  />
                </div>
              </div>

              <div>
                <label className="block font-semibold mb-1">Service Item Description</label>
                <input
                  type="text"
                  value={serviceDescription}
                  onChange={(e) => setServiceDescription(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-medium"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block font-semibold mb-1">Currency &amp; Rate</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      disabled
                      value={currency}
                      className="w-16 px-2 py-2 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 font-mono text-center font-bold"
                    />
                    <input
                      type="number"
                      value={serviceRate}
                      onChange={(e) => setServiceRate(Number(e.target.value))}
                      className="flex-1 px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-mono font-bold"
                    />
                  </div>
                </div>
                {currency !== "INR" && (
                  <div>
                    <label className="block font-semibold mb-1">FX Rate to INR (1 {currency} = ₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      value={fxRate}
                      onChange={(e) => setFxRate(Number(e.target.value))}
                      className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-mono"
                    />
                  </div>
                )}
                {customerCountry !== "IN" && (
                  <div>
                    <label className="block font-semibold mb-1">Withholding Tax (WHT %)</label>
                    <input
                      type="number"
                      min="0"
                      max="30"
                      value={whtPercent}
                      onChange={(e) => setWhtPercent(Number(e.target.value))}
                      placeholder="0"
                      className="w-full px-3 py-2 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 font-mono"
                    />
                  </div>
                )}
              </div>

              {/* Dynamic Live Preview Box */}
              <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 space-y-1.5">
                <div className="flex items-center justify-between font-bold text-slate-800 dark:text-slate-200">
                  <span>Output Document:</span>
                  <span className={sellerProfile.gstStatus === "unregistered" ? "text-cyan-600" : "text-emerald-600"}>
                    {sellerProfile.gstStatus === "unregistered" ? "Bill of Supply (Zero GST)" : "Tax Invoice (GST Active)"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-slate-500 text-[11px]">
                  <span>Total Payable:</span>
                  <span className="font-mono font-bold">
                    {currency} {Number(serviceRate).toLocaleString()}
                  </span>
                </div>
                {currency !== "INR" && (
                  <div className="flex items-center justify-between text-slate-500 text-[11px]">
                    <span>Turnover Addition (INR):</span>
                    <span className="font-mono font-bold text-primary">
                      ₹{(Number(serviceRate) * fxRate).toLocaleString("en-IN")}
                    </span>
                  </div>
                )}
              </div>

              <div className="flex items-center justify-end gap-3 pt-3 border-t border-slate-100 dark:border-slate-800">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsCreateModalOpen(false)}
                  className="cursor-pointer"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={creatingInvoice}
                  size="sm"
                  className="bg-primary text-white font-bold cursor-pointer"
                >
                  {creatingInvoice ? "Issuing..." : "Confirm & Issue Invoice"}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
